import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileEdit, CheckCircle2, XCircle, Clock, Loader2, History } from "lucide-react";
import { toast } from "sonner";

const STATUS_UI = {
  pending: { label: "En attente", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  approved: { label: "Approuvée", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  rejected: { label: "Rejetée", color: "bg-red-100 text-red-800", icon: XCircle },
};

export default function GradeAdjustmentsPage() {
  const [rows, setRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [decideDlg, setDecideDlg] = useState(null);
  const [form, setForm] = useState({ approved: true, note: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, h] = await Promise.allSettled([
      api.get("/pedagogy/grade-adjustments"),
      api.get("/pedagogy/grade-history"),
    ]);
    if (a.status === "fulfilled") setRows(a.value.data);
    else toast.error("Erreur de chargement des demandes");
    if (h.status === "fulfilled") setHistory(h.value.data);
  };
  useEffect(() => { load(); }, []);

  const decide = async () => {
    setSaving(true);
    try {
      await api.post(`/pedagogy/grade-adjustments/${decideDlg.id}/decide`, form);
      toast.success(form.approved ? "Réajustement approuvé — note mise à jour" : "Demande rejetée");
      setDecideDlg(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="grade-adjustments-page">
      <div>
        <div className="chip mb-2"><FileEdit className="h-3 w-3" /><span>RÉAJUSTEMENTS DE NOTES</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Demandes de réajustement</h1>
        <p className="text-slate-500 mt-1">Demandes de modification de notes validées, soumises par les enseignants. Chaque décision est historisée.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Élève</th>
                <th className="px-4 py-3 text-left">Matière / Période</th>
                <th className="px-4 py-3 text-right">Ancienne → Nouvelle</th>
                <th className="px-4 py-3 text-left">Motif</th>
                <th className="px-4 py-3 text-left">Demandé par</th>
                <th className="px-4 py-3 text-center">Statut</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const st = STATUS_UI[r.status] || STATUS_UI.pending;
                const Ico = st.icon;
                return (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`adj-row-${r.id}`}>
                    <td className="px-4 py-2 font-medium">{r.student_name}<div className="text-xs text-slate-400">{r.class_name}</div></td>
                    <td className="px-4 py-2 text-xs">{r.subject}<div className="text-slate-400">{r.period}</div></td>
                    <td className="px-4 py-2 text-right tabular-nums"><span className="text-red-600 line-through">{r.old_value}</span> → <strong className="text-[#166534]">{r.new_value}</strong>/{r.max_value}</td>
                    <td className="px-4 py-2 max-w-xs truncate text-xs">{r.reason}</td>
                    <td className="px-4 py-2 text-xs">{r.requested_by_name}<div className="text-slate-400">{new Date(r.requested_at).toLocaleString("fr-FR")}</div></td>
                    <td className="px-4 py-2 text-center"><span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold ${st.color}`}><Ico className="h-3 w-3" />{st.label}</span></td>
                    <td className="px-4 py-2 text-right">
                      {r.status === "pending" && (
                        <Button size="sm" onClick={() => { setDecideDlg(r); setForm({ approved: true, note: "" }); }} className="bg-[#1E3A8A] text-white" data-testid={`adj-decide-${r.id}`}>Décider</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Aucune demande de réajustement.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A] flex items-center gap-2">
          <History className="h-4 w-4" />Historique des modifications de notes
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Date & heure</th>
                <th className="px-4 py-2 text-left">Élève</th>
                <th className="px-4 py-2 text-left">Matière</th>
                <th className="px-4 py-2 text-right">Ancienne → Nouvelle</th>
                <th className="px-4 py-2 text-left">Auteur</th>
                <th className="px-4 py-2 text-left">Motif</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id} className="border-t border-slate-100" data-testid={`hist-${h.id}`}>
                  <td className="px-4 py-2 text-xs text-slate-500">{new Date(h.at).toLocaleString("fr-FR")}</td>
                  <td className="px-4 py-2 font-medium">{h.student_name}</td>
                  <td className="px-4 py-2 text-xs">{h.subject} <span className="text-slate-400">({h.period})</span></td>
                  <td className="px-4 py-2 text-right tabular-nums"><span className="text-red-600 line-through">{h.old_value}</span> → <strong className="text-[#166534]">{h.new_value}</strong></td>
                  <td className="px-4 py-2 text-xs">{h.author_name}{h.decided_by_name && <div className="text-slate-400">Approuvé par {h.decided_by_name}</div>}</td>
                  <td className="px-4 py-2 text-xs max-w-xs truncate">{h.reason}</td>
                </tr>
              ))}
              {history.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Aucune modification historisée.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Decide dialog */}
      <Dialog open={!!decideDlg} onOpenChange={(o) => !o && setDecideDlg(null)}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Décision — {decideDlg?.student_name}</DialogTitle></DialogHeader>
          {decideDlg && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <div><strong>{decideDlg.subject}</strong> ({decideDlg.period}) : {decideDlg.old_value} → <strong>{decideDlg.new_value}</strong>/{decideDlg.max_value}</div>
                <div className="mt-1 text-xs"><strong>Motif :</strong> {decideDlg.reason}</div>
                <div className="mt-1 text-xs text-slate-400">Demandé par {decideDlg.requested_by_name}</div>
              </div>
              <div className="flex gap-2">
                <Button variant={form.approved ? "default" : "outline"} className={form.approved ? "bg-[#166534] text-white flex-1" : "flex-1"} onClick={() => setForm({ ...form, approved: true })} data-testid="adj-approve">Approuver</Button>
                <Button variant={!form.approved ? "default" : "outline"} className={!form.approved ? "bg-red-600 text-white flex-1" : "flex-1"} onClick={() => setForm({ ...form, approved: false })} data-testid="adj-reject">Rejeter</Button>
              </div>
              <div>
                <Label>Note du Directeur (optionnel)</Label>
                <Textarea rows={2} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} data-testid="adj-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecideDlg(null)}>Annuler</Button>
            <Button onClick={decide} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="adj-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Valider la décision"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
